import type { WeightUnitLabels } from "@heraion/shared";
import type { TFn } from "./i18nTypes";

// The weight unit as a human reads it — "kg" / "lb" in four of our languages,
// «кг» / «фунт» in Russian.
//
// ⭐ Hoisted here by CN-26 wave 2 from `Logger/plateCalcMath.ts`, which is
// where `unitWord` was born and whose docblock named this move: *"taking the
// unit out of formatWeight is CN-26 ondata 2, not this slice"*. It was never
// a plate-calculator concept — it only lived there because the plate
// calculator was the first screen that needed the unit spelled correctly.
// Every weight in the app now reads it from here.
//
// The key is `common.weightUnit_*` (moved out of the plates namespace by
// CN-36, once the string stopped being plates-only).
//
// ⚠️ The key is built by INTERPOLATION, so the i18n drift gate — which greps
// for `t('literal.key')` — walks straight past it. The `defaultValue` is the
// only net, which is the whole reason there is exactly one call site for each
// unit and it is in this file.

/**
 * One unit, as a translated word. Falls back to the canonical abbreviation,
 * which is also what four of the five locales say.
 */
export const unitWord = (t: TFn, unit: "kg" | "lb"): string =>
  t(`common.weightUnit_${unit}`, { defaultValue: unit });

/**
 * Both units, in the bundle shape that `@heraion/shared`'s weight formatters
 * take. Build it once per render and hand it to `formatWeight` / `formatVolume`
 * / `formatE1rm` / `formatWeightCompact`.
 *
 * ⛔ `packages/shared` cannot import `@heraion/i18n` (it depends on zod and
 * nothing else, and that edge is not being added), so the translated words
 * have to travel INTO the formatter as data. This is the same shape
 * `shareCardFormat.ts` already uses for its `ShareCardLabels`.
 */
export const weightUnitLabels = (t: TFn): WeightUnitLabels => ({
  kg: unitWord(t, "kg"),
  lb: unitWord(t, "lb"),
});
